import { MimeType } from "@/type/core/api/api.types";

const removeBase64UrlPrefix = (base64File: string): string => {
  const i = base64File.indexOf("base64,");
  return base64File.slice(i + 7);
};

export const addBase64UrlPrefix = (
  base64: string,
  contentType: string,
): string => {
  return `data:${contentType};base64,${base64}`;
};

export const base64ToBlob = (base64: string, contentType: string): Blob => {
  const byteNumbers = new Array(base64.length);
  for (let i = 0; i < base64.length; i++) {
    byteNumbers[i] = base64.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

export const getBase64 = (
  img: Blob,
  callback: (imageUrl: string | ArrayBuffer | null) => void,
): void => {
  const reader = new FileReader();
  reader.addEventListener("load", () =>
    callback(removeBase64UrlPrefix(reader.result as string)),
  );
  reader.readAsDataURL(img);
};

export const getBase64Async = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    getBase64(file, (result) => {
      if (result) {
        resolve(result as string);
      } else {
        reject(new Error("File could not be read"));
      }
    });
  });
};

export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject; // Triggered if there's an error reading the file.
    reader.onload = () => {
      resolve(reader.result as string); // The result contains the Base64-encoded data.
    };
    reader.readAsDataURL(blob); // Initiates the read operation.
  });

export const getBase64UrlFrom = (base64: string, type: string): string => {
  return `data:${type};base64,${base64}`;
};

/**
 * Convert an array of numbers to a File
 * @param numberArray - Array of numbers
 * @param fileName - Desired file name
 * @param fileType - Desired file type (MIME type)
 * @returns File object
 */
export const numberArrayToFile = (
  numberArray: number[],
  fileName: string,
  fileType: MimeType,
): File => {
  // Convert the array of numbers to a Uint8Array
  const uint8Array = new Uint8Array(numberArray);

  // Create a Blob from the Uint8Array
  const blob = new Blob([uint8Array], { type: fileType });

  // Create a File from the Blob
  return new File([blob], fileName, { type: fileType });
};

export function convertImageToBlob(imageSrc: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(image, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Conversion to blob failed"));
          }
        }, "image/png");
      } else {
        reject(new Error("Canvas context is not available"));
      }
    };

    image.onerror = () => {
      reject(new Error("Failed to load image"));
    };
  });
}

export const numberArrayToBase64 = (numberArray: number[]): string => {
  // Convert the array of numbers to a Uint8Array
  const uint8Array = new Uint8Array(numberArray);

  // Convert the Uint8Array to a binary string
  let binaryString = "";
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }

  // Encode the binary string to a Base64 string
  return btoa(binaryString);
};
